import ServiceModule from '@modules/ServiceModule';
import FeatureFlagModule, { FeatureFlagKeys } from '@modules/FeatureFlagModule';
import { PcsConversationType } from '@modules/SoftPhoneModule/lib/getPcsConversation';
import { WebphonePhoneStateEvent } from '@modules/SoftPhoneModule/types';
import TasksModule from '../../TasksModule';
import IAppLogger from '@app/lib/IAppLogger';
import InboxModule from '@modules/InboxModule';
import { ITask } from '@modules/TasksModule/types';
import Task from '@modules/TasksModule/Task';
import { ConversationTrait } from '@api/generated/graphql';
import {
  fetchCampaignForTheCall,
  fetchWorkflowNameForTheCall,
} from '../services';
import ToastModule from '@modules/ToastModule';
import { isValidPhoneNumber } from '@pages/settings/phoneNumberUtils';
import FrontEndSettingsModule from '@modules/FrontEndSettingsModule';

/**
 * Determines if a given task represents a transfer of an incoming voice call.
 *
 * @param task - The task object containing attributes related to the call.
 * @param conversation - The conversation object containing details about the call.
 * @returns A boolean indicating whether the task is an incoming transfer voice call.
 *
 * The function checks the following conditions:
 * - The conversation originates from an external source (`ConversationTrait.External`).
 * - Multiple agents are involved in the conversation (`agentIds.length > 1`). (This could also indicate an internal call! Meaningless without the external trait.)
 * - The current agent has not initiated the transfer (`task.attributes.transferStatus` is undefined).
 *
 * If all conditions are met, the task is considered a transfer of an incoming voice call.
 */
export const isTransferVoiceTask = (
  task: ITask,
  conversation: PcsConversationType,
): boolean => {
  const { agentIds, traits } = conversation;
  const isExternalSource = traits?.includes(ConversationTrait.External);
  const hasMultipleAgents = agentIds.length > 1;
  const isTransferInitiatedByThisAgent =
    task.attributes.transferStatus !== undefined;

  return (
    isExternalSource && hasMultipleAgents && !isTransferInitiatedByThisAgent
  );
};

/**
 * Resolves queue info from PCS and sets it on the task.
 * Only called when RTCS is down (no session), so queueId/queueName are not yet set.
 */
const resolveQueueFromPcs = (
  task: Task,
  queueIds: string[],
  logger: IAppLogger,
): void => {
  if (queueIds.length === 0) return;
  const latestQueueId = queueIds[queueIds.length - 1];
  const queue = ServiceModule.getQueue(latestQueueId);
  if (!queue) {
    logger.error(`Queue not found for queueId: ${latestQueueId}`);
    return;
  }
  task.queueId = queue.id;
  task.queueName = queue.name;
};

/**
 *  This function is called when a PcsConversation is fetched for a callId.
 *  It updates the task with session related information and the PcsConversation data.
 */
const handlePcsConversationFetched = (
  pcsConversation: PcsConversationType,
  phoneStateUpdateEvent: WebphonePhoneStateEvent,
  logger: IAppLogger,
) => {
  if (!pcsConversation) {
    logger.error(
      `PcsConversation not found for remoteCallId ${phoneStateUpdateEvent.callState.remoteCallId}}`,
    );
    return;
  }
  const { callId } = phoneStateUpdateEvent.callState;

  // get the task for the callId, a task was created with either pcsConversationSid or callId when a session offered event received
  // or a softphone call.created event received
  const task =
    TasksModule.getTask(callId) ?? TasksModule.getTask(pcsConversation.sid);

  if (!task) {
    logger.error(`task not found for callId: ${callId}}`);
    return;
  }

  const { sid: pcsConversationSid, traits } = pcsConversation;
  const session = ServiceModule.getSessionByConversationSid(pcsConversationSid);
  const taskTenantId = session?.tenantId || pcsConversation.tenantId;

  // if there is a session, update task with session attributes
  if (session) {
    InboxModule.setSessionProperties(task.id, session, session.target.address);
  }

  // todo: remove this till we have a better way to determine if the task is an incoming transfer
  // if (
  //   FeatureFlagModule.isEnabled(
  //     FeatureFlagKeys.featureInboxCallTransferToAgent,
  //   ) &&
  //   isTransferVoiceTask(task, pcsConversation)
  // ) {
  //   TasksModule.setTaskAttribute(task.id, 'incomingTransfer', true);
  // }

  // update task with information from the pcsConversation
  TasksModule.setTaskAttribute(task.id, 'sessionSid', pcsConversationSid);
  TasksModule.setTaskAttribute(task.id, 'traits', traits);

  // When RTCS is down (no session), resolve queue info from PCS before type
  // evaluation so that evaluateAndSetTaskType can use task.queueId as a fallback
  // signal. When a session exists, queueId is already authoritative from RTCS via
  // setSessionProperties above, so we skip the PCS lookup to avoid overwriting it.
  if (!session) {
    resolveQueueFromPcs(task, pcsConversation.queueIds, logger);
  }

  InboxModule.evaluateAndSetTaskType(task.id);

  if (task.id !== pcsConversationSid) {
    // remove the incoming call toast for the callId, a new callToast will be created for the pcsConversationSid
    const tasks = TasksModule.getTasks();
    const hasMultipleTasks =
      tasks.filter((t) => t.attributes.callId === task.attributes.callId)
        .length > 1;
    if (hasMultipleTasks) {
      ToastModule.hideIncomingCallToast(task.attributes.callId);
    }

    // update task id with pcsConversationSid if task was created via the callId; otherwise this would cause the task to be deleted
    TasksModule.updateTaskId(task, pcsConversationSid);
  }

  // we want to start sending interaction data for all calls
  // we should only do this when we have the conversationSid to prevent duplicate data
  // as outbounds are created with callId and then updated with the conversationSid
  TasksModule.setTaskAttribute(pcsConversationSid, 'recordInteraction', true);

  // set tenantId for all calls
  InboxModule.setTenantIdForTask(task, taskTenantId);

  InboxModule.setSelectedTaskId(pcsConversationSid);

  // remove the incoming call toast for the callId, a new callToast will be created for the pcsConversationSid
  // ToastModule.hideIncomingCallToast(task.attributes.callId);

  // Fetch campaign for queue calls
  const isValidTenant = +taskTenantId > 0; // For Internal call tenantId = "0"
  const isValidTargetPhoneNumber =
    session?.target?.address && isValidPhoneNumber(session?.target?.address);
  if (
    FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureDisplayCampaignNameOnTaskAndToast,
    ) &&
    isValidTenant &&
    isValidTargetPhoneNumber
  ) {
    fetchCampaignForTheCall({
      tenantId: taskTenantId,
      toPhoneNumber: session.target.address,
      taskId: task.id,
    });
  }

  // Fetch workflow name for calls
  if (
    FrontEndSettingsModule.isContactCenterSettingsEnabled(
      'showWorkflowNameOnInboundCalls',
    ) &&
    isValidTargetPhoneNumber
  ) {
    fetchWorkflowNameForTheCall({
      toPhoneNumber: session.target.address,
      taskId: task.id,
    });
  }
};

export default handlePcsConversationFetched;
